import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@mui/material/styles';

import AppDialog from '../../../components/reusable/app-dialog/AppDialog';
import { useBackLayerStack } from '../../../context/BackLayerStackContext';
import { useGetAvailableExercises } from '../../../redux/api/exercisesAPI';
import { IExercise } from '../../../types/interfaces/exercise';
import { useStyles } from './NewExcercise.styles';
import NewExerciseForm from './NewExerciseForm';

interface INewExerciseModalProps {
  showNewExerciseModal: boolean;
  setShowNewExerciseModal: (value: boolean) => void;
  onExerciseCreate: (value: IExercise) => void;
}

export function NewExerciseModal({
  showNewExerciseModal,
  setShowNewExerciseModal,
  onExerciseCreate,
}: INewExerciseModalProps) {
  const theme = useTheme();
  const { classes } = useStyles(theme);
  const [newExerciseId, setNewExerciseId] = useState<string>(null);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const { t } = useTranslation();
  const { data: exercises } = useGetAvailableExercises();
  const { tryDismissTop } = useBackLayerStack();

  useEffect(() => {
    if (!exercises || !newExerciseId) return;

    const newExercise = exercises.find(
      (exercise) => exercise.id === newExerciseId,
    );

    if (newExercise) {
      onExerciseCreate(newExercise);
      setShowNewExerciseModal(false);
      setNewExerciseId(null);
      setIsFormDirty(false);
    }
  }, [newExerciseId, exercises]);

  function handleClose(forceClose?: unknown) {
    const shouldForceClose = forceClose === true;
    if (!shouldForceClose && isFormDirty && tryDismissTop()) {
      return;
    }
    setShowNewExerciseModal(false);
    setIsFormDirty(false);
  }

  return (
    <AppDialog
      classes={{ root: classes.newExerciseDialog }}
      open={!!showNewExerciseModal}
      onClose={() => handleClose()}
      title={t('exercises.newExercise')}>
      <NewExerciseForm
        onExerciseCreate={(id) => setNewExerciseId(id)}
        onCancelClick={() => handleClose(true)}
        onDirtyChange={setIsFormDirty}
      />
    </AppDialog>
  );
}
